<template>
  <practitioner-layout v-if="render">
    <router-view @refresh="getData"></router-view>
    <v-container fluid>
      <grid
        v-if="showGrid"
        :headers-preset-type="headersPresetType"
        :headers-preset="headersPreset"
        :data="items"
        :loading="loading"
        @rowAdd="rowAdd"
        @refresh="getData"
        @rowClick="rowClicked"
        :disable-pagination="false"
        :hide-default-footer="loading"
        @grid-options-changed="gridOptionsChanged"
        :server-items-length="rowsNumber"
        @grid-search="gridSearch"
        :disable-sort="false"
        :hide-filters="true"
        :search-value.sync="filter.searchValue"
        :show-grid-settings="true"
      >
        <template v-slot:filters>
          <v-col v-if="showFilters" cols="12" lg="8" class="mt-md-1">
            <DatePicker
              v-model="filter.dateRange"
              @input="getData(true)"
            />
          </v-col>
        </template>
      </grid>
    </v-container>
  </practitioner-layout>
</template>

<script>
import Grid from '@components/common/Grid.vue'
import DatePicker from '@components/common/grid-elements/DatePicker.vue'

import PractitionerLayout from '@router/layouts/practitioner.vue'
import appointmentsPracticesHeaders from '@src/data/dataset/gridHeaders/appointmentPractices'
import moment from 'moment'

export default {
  name: 'AppointmentGrid',
  components: {
    Grid,
    DatePicker,
    PractitionerLayout
  },
  data () {
    return {
      items: [],
      loading: false,
      headersPresetType:'appointmentPractices',
      headersPreset:appointmentsPracticesHeaders,
      render:true,
      gridOptions: {},
      rowsNumber: 0,
      gridSearchPhrase: '',
      restRequest: null,
      onlyCurrentPractice: false,
      filterData: {
        appointmentStatuses: []
      },
      filter: {
        searchValue: '',
        dateRange: null
      },
      specialitiesList: [],
      appointmentStatuses: ['N'],
      getDataRequest: undefined,
      showGrid: false
    }
  },
  methods: {
    getAppointmentStatuses() {
      this.$_rest.get(`/practitioners/${this.$store.getters['app/currentUserId']}/appointment-statuses`, {
        isActive: 1
      }, response => {
        this.filterData.appointmentStatuses = response.data.filter(status => status.nameWeb).sort((a, b) => (a.nameWeb > b.nameWeb) ? 1 : -1)
      }, error => {
        console.error(error)
        this.$_notify.error('Failed to load appointment statuses')
      }, {
        prefixRoutesWithOrganizationId: false
      });
    },
    getData (resetPage = false) {
      if (!this.filter.dateRange) {
        this.$_notify.error(this.$_t('Date range must be selected'));
        return;
      }
      if (this.filter.dateRange.length === 1) {
        this.filter.dateRange.push(this.filter.dateRange[0])
      }
      if (resetPage) {
        this.gridOptions.page = 1
      }

      this.items = []
      this.loading = true
      this.$store.dispatch(
        'appointment/getNonClinicalAppointments',
        {gridOptions: this.gridOptions, gridFilter: this.gridFilter, onlyCurrentPractice: this.onlyCurrentPractice}
      ).then((response) => {
        this.rowsNumber = response.meta.rowsNumber
        this.items = this.mapDatasetResponse(response)
        this.$store.dispatch('appointment/setNonClinicalAppointmentCount', this.rowsNumber)
        this.$store.dispatch('appointment/setAppointmentsList', this.items).then(() => {
          this.loading = false
        })
      })
    },
    rowAdd () {
    },
    rowClicked (row) {
      this.$store.commit('appointment/setAppointmentsList', this.items)
      this.$router.push({ name: 'non-clinical-appointment-details', params: { id: row.appointmentId, source: 'non-clinical-appointments' } })
    },
    gridOptionsChanged(options) {
      this.gridOptions = options
      this.getData()
    },
    gridSearch(val) {
      this.gridSearchPhrase = val
      this.getData(true)
    },
    fetchAllSpecialities(){
      const practitionerId = this.$store.getters['app/practitioner'].id
      return new Promise((resolve, reject) => {
        this.$_rest.get(`/practitioners/${ practitionerId }/specialities`, {
          groups: ['all', 'practitioner_speciality', 'practitioner_all', 'practitioner_practice_default_speciality'].join(','),
          limit: 10000
        }, (response) => {
          this.specialitiesList = response.data ? response.data : []
          resolve(response)
        }, error => {
          reject(error)
        })})
    },
    cacheDateRange() {
      sessionStorage.setItem('gridDateRange', JSON.stringify(this.filter.dateRange))
    },
    initDateRange() {
      this.filter.dateRange = [
        moment().subtract(2, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ]
    }
  },
  computed:{
    refreshed() {
      return this.$store.getters['app/getRefreshed']
    },
    showFilters() {
      return true;
    },
    gridFilter() {
      let filter = {}
      if(this.onlyCurrentPractice){
        filter = {
          practitionerPracticeId: this.$store.getters['app/practitionerPractice'].id,
        }
      } else {
        filter = {
          practitionerId: this.$store.getters['app/practitioner'].id,
        }
      }

      const searchableColumns = [
        'searchPatientFullName',
        'searchClinicNameShort',
        'searchProcedureName',
        'searchInsurerName',
        'searchClinicScanRoomCode',
        'searchPatientNumber1',
        'searchPatientIdShort',
        'searchPatientIdExternal',
        'searchAppointmentIdShort',
        'searchPatientLocationName'
      ];

      if (this.gridSearchPhrase) {
        searchableColumns.forEach((col) => {
          filter[col] = {
            type: 'like',
            x: this.gridSearchPhrase,
            connector: 'OR'
          }
        })
      }

      if (this.filter.dateRange && this.filter.dateRange.length === 2) {
        this.cacheDateRange()
        let range = this.filter.dateRange
        range = range.sort()
        filter['createdDateFrom'] = { type: 'gt', x: range[0]}
        //to date does not include apts of that day, even with lte type, add 1 day
        const toDate = new Date(range[1])
        toDate.setDate(toDate.getDate() + 1)
        filter['createdDateTo'] = { type: 'lte', x: moment(toDate).format('YYYY-MM-DD')}
      }

      return filter
    }
  },
  watch:{
    refreshed(val) {
       if(val) {
        this.render = false
        this.$nextTick(()=>{
          this.render = true
          this.$store.commit('app/setRefreshed',false)
        })
      }
    },
    onlyCurrentPractice(){
      this.getData()
    }
  },
  mounted () {
    if (this.$store.getters['app/practitionerPractice']) {
      this.showGrid = true
      if (this.showFilters) {
        this.getAppointmentStatuses()
        this.fetchAllSpecialities()
      }
    }
    const gridDateRange = sessionStorage.getItem('gridDateRange')
    if (gridDateRange) {
      try {
        this.filter.dateRange = JSON.parse(gridDateRange)
      } catch (e) {
        this.initDateRange()
      }
    } else {
      this.initDateRange()
    }
  }
}
</script>
